import {useEffect, useMemo, useRef, useState} from 'react'

import {MESSAGE_ROLES} from '../config.js'

const SKELETON_ROW_HEIGHT = 24
const SKELETON_ROW_GAP = 8
const SKELETON_GROUP_GAP = 24
const SKELETON_ROW_TOTAL = SKELETON_ROW_HEIGHT + SKELETON_ROW_GAP

const MAX_ROWS_PER_GROUP = 3

const WIDTHS_BY_ROLE = {
  [MESSAGE_ROLES.USER]: ['58%', '46%', '40%', '54%', '32%', '50%'],
  [MESSAGE_ROLES.ASSISTANT]: ['66%', '72%', '53%', '68%', '45%', '63%', '55%', '71%']
}

const ROLES_SEQUENCE = [MESSAGE_ROLES.USER, MESSAGE_ROLES.ASSISTANT]

const buildSkeletonGroups = availableHeight => {
  if (availableHeight <= 0) return []

  const groups = []
  let remaining = availableHeight
  let groupIndex = 0

  while (remaining >= SKELETON_ROW_TOTAL) {
    const role = ROLES_SEQUENCE[groupIndex % ROLES_SEQUENCE.length]
    const widths = WIDTHS_BY_ROLE[role]
    const gap = groups.length > 0 ? SKELETON_GROUP_GAP : 0

    if (remaining < gap + SKELETON_ROW_HEIGHT) break

    remaining -= gap

    const maxRows = Math.floor((remaining + SKELETON_ROW_GAP) / SKELETON_ROW_TOTAL)
    const rowsCount = Math.min(maxRows, MAX_ROWS_PER_GROUP)

    if (rowsCount <= 0) break

    groups.push({
      role,
      widths: Array.from({length: rowsCount}, (_, index) => widths[index % widths.length])
    })

    remaining -= rowsCount * SKELETON_ROW_TOTAL
    groupIndex++
  }

  return groups
}

export const useSkeletonGroups = () => {
  const containerRef = useRef(null)
  const [availableHeight, setAvailableHeight] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    setAvailableHeight(Math.floor(el.clientHeight))

    const observer = new ResizeObserver(([entry]) => {
      const nextHeight = Math.floor(entry.contentRect.height)
      setAvailableHeight(currentHeight => (currentHeight === nextHeight ? currentHeight : nextHeight))
    })

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const groups = useMemo(() => buildSkeletonGroups(availableHeight), [availableHeight])

  return {containerRef, groups}
}
